import { Injectable } from '@angular/core';
import * as URI from 'urijs';

import { AppConfig } from '®shared/config';

export const AVATAR_SIZE = 35;

@Injectable()
export class AvatarsService {
  private colour: string;
  private size = AVATAR_SIZE;
  PERSONA_HOST = this.appConfig.personaEndpoint;

  constructor(private appConfig: AppConfig) {}

  /**
   * Returns a URL to a Persona-generated avatar
   *
   * @param {string} guid - the users GUID.
   * @param {string=} size - dimensions of the requested avatar (default = 36px)
   * @param {string=} colour - background colour of avatar
   * @return {string} The URL to the generated avatar
   **/
  public getImageURL(guid, size = this.size, colour = this.colour): string {
    return this.getPersonaAvatarUri(guid)
      .search({ size: size.toString(), colour })
      .toString();
  }

  /**
   * Builds the appropriate srcset string to return a normal and HiDPI version of the avatar
   *
   * @param {string} guid - the users GUID.
   * @param {string=} size - dimensions of the requested avatar (default = 36px)
   * @param {string=} colour - the desired background colour of the avatar
   * @return {string} A formatted srcset string
   **/
  public getSrcSetValue(guid, size = this.size, colour = this.colour): string {
    const avatarUri = this.getPersonaAvatarUri(guid);

    const loDpiAvatar = avatarUri
      .clone()
      .search({ size: size.toString(), colour });
    const hiDpiAvatar = avatarUri
      .clone()
      .search({ size: (size * 2).toString(), colour });

    return `${loDpiAvatar} 1x, ${hiDpiAvatar} 2x`;
  }

  /**
   * Constructs a base URI for the given user's avatar.
   *
   * @param {string} guid - the users GUID.
   * @return {URI} A base URI for the user's avatar
   **/
  private getPersonaAvatarUri(guid: string): URI {
    return URI(this.PERSONA_HOST).segment(`users/${guid}/avatar`);
  }
}
